/**
 * GuidedTour — la visite guidée de l'application.
 *
 * Un carrousel modal de dix étapes qui explique où se trouve quoi, dans la
 * langue du membre. Les étapes qui dépendent d'une fonctionnalité désactivée
 * (publication, comptes connectés) disparaissent de la visite.
 *
 * Déclenchement :
 *  - automatique à la première ouverture de l'app par un membre (une seule
 *    fois, mémorisé par utilisateur dans localStorage) ;
 *  - à la demande, via `openGuidedTour()` — utilisé par la carte du tableau
 *    de bord et par la page Profil.
 *
 * Le drapeau est local au navigateur : rien n'est envoyé au serveur, et un
 * membre qui change d'appareil reverra simplement la visite une fois.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  ArrowRight,
  ArrowLeft,
  LayoutGrid,
  UserSearch,
  ClipboardList,
  Compass,
  Sparkles,
  CalendarClock,
  MessageSquare,
  Plug,
  Bot,
  Users,
  Package,
  Zap,
  FlaskConical,
  BarChart3,
  BellRing,
} from 'lucide-react';
import { IneaMark } from '@/components/IneaMark';
import { useFeatures } from '@/hooks/useFeatures';
import { useAuth } from '@/hooks/useAuth';
import { useAutomationsAccess } from '@/lib/automations';

const EVENT = 'nia:guided-tour';
const STORAGE_PREFIX = 'nia-tour-done';

/** Ouvre la visite guidée depuis n'importe où dans l'app. */
export function openGuidedTour(): void {
  window.dispatchEvent(new CustomEvent(EVENT));
}

function storageKey(userId: string | undefined): string {
  return `${STORAGE_PREFIX}:${userId ?? 'anon'}`;
}

/** True si ce membre a déjà vu (ou passé) la visite sur ce navigateur. */
export function hasSeenTour(userId: string | undefined): boolean {
  try {
    return localStorage.getItem(storageKey(userId)) === '1';
  } catch {
    return true; // stockage indisponible : on n'insiste pas
  }
}

function markSeen(userId: string | undefined): void {
  try {
    localStorage.setItem(storageKey(userId), '1');
  } catch {
    /* stockage indisponible : sans conséquence */
  }
}

interface Step {
  key: string;
  icon: typeof LayoutGrid;
  tint: string;
  /** Fonctionnalité requise pour que l'étape ait un sens. */
  needs?: 'publishing';
}

const STEPS: Step[] = [
  { key: 'welcome', icon: Bot, tint: '#a855f7' },
  { key: 'dashboard', icon: LayoutGrid, tint: '#3b82f6' },
  { key: 'audit', icon: UserSearch, tint: '#ec4899' },
  { key: 'pipeline', icon: ClipboardList, tint: '#a855f7' },
  { key: 'identity', icon: Compass, tint: '#3b82f6' },
  { key: 'content', icon: Sparkles, tint: '#f59e0b' },
  { key: 'calendar', icon: CalendarClock, tint: '#a855f7', needs: 'publishing' },
  { key: 'conversations', icon: MessageSquare, tint: '#06b6d4' },
  { key: 'accounts', icon: Plug, tint: '#10b981', needs: 'publishing' },
  { key: 'inea', icon: Bot, tint: '#ec4899' },
];

/**
 * Visite de la community manager (lot 6, 2026-10-29) : compte du CRM M&R dont l'espace a le module
 * Automatisations. Dix étapes dans l'ordre de mise en route — espace partagé, comptes, contenus, fiches
 * produits, automatisation, test, boîte de réception, statistiques, équipe.
 */
const CM_STEPS: Step[] = [
  { key: 'cmWelcome', icon: Bot, tint: '#a855f7' },
  { key: 'cmWorkspace', icon: Users, tint: '#8b5cf6' },
  { key: 'cmAccounts', icon: Plug, tint: '#10b981', needs: 'publishing' },
  { key: 'cmContent', icon: Sparkles, tint: '#f59e0b' },
  { key: 'cmProducts', icon: Package, tint: '#ec4899' },
  { key: 'cmAutomation', icon: Zap, tint: '#8b5cf6' },
  { key: 'cmTest', icon: FlaskConical, tint: '#f59e0b' },
  { key: 'cmInbox', icon: MessageSquare, tint: '#06b6d4' },
  { key: 'cmStats', icon: BarChart3, tint: '#3b82f6' },
  { key: 'cmTeam', icon: BellRing, tint: '#ec4899' },
];

export function GuidedTour() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { publishing_enabled: publishingEnabled, automations_enabled: automationsEnabled, loaded } = useFeatures();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  // Lot 6 — un compte du CRM M&R dont l'espace a le module Automatisations suit la visite de la community
  // manager ; on attend la réponse de l'allow-list avant d'ouvrir, pour ne jamais montrer la mauvaise visite.
  const cmCandidate = !!user && user.origin === 'mr_crm' && automationsEnabled === true;
  const access = useAutomationsAccess(cmCandidate);
  const cm = cmCandidate && access.data?.enabled === true;
  const ready = loaded && (!cmCandidate || access.isFetched);

  const steps = useMemo(
    () => (cm ? CM_STEPS : STEPS).filter((s) => s.needs !== 'publishing' || publishingEnabled),
    [cm, publishingEnabled],
  );

  // Ouverture à la demande (carte du tableau de bord, page Profil).
  useEffect(() => {
    const onOpen = (): void => {
      setIndex(0);
      setOpen(true);
    };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    markSeen(user?.id);
  }, [user?.id]);

  // Clavier : Échap ferme, flèches naviguent.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, steps.length - 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, steps.length]);

  const step = steps[Math.min(index, steps.length - 1)];
  if (!open || !ready || !step) return null;

  const Icon = step.icon;
  const isLast = index === steps.length - 1;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="tour"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/45 p-4 backdrop-blur-[3px] sm:items-center"
        onClick={close}
        role="dialog"
        aria-modal="true"
        aria-label={t('tour.title') as string}
        data-tour={cm ? 'cm' : 'general'}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ type: 'spring', damping: 30, stiffness: 320 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-[560px] overflow-hidden rounded-[26px] border border-white/70 bg-white shadow-[0_30px_70px_-24px_rgba(10,18,38,0.35)]"
        >
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-1"
            style={{ background: 'linear-gradient(90deg,#3b82f6,#a855f7,#ec4899)' }}
          />

          <header className="flex items-center gap-3 px-5 pt-5 sm:px-7">
            <IneaMark size={34} />
            <div className="flex-1">
              <p className="font-mono text-[10px] font-700 uppercase tracking-[0.18em] text-ink/50">
                {t('tour.title')}
              </p>
              <p className="text-[12px] text-ink/55">
                {t('tour.step', { n: index + 1, total: steps.length })}
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label={t('common.close') as string}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-ink/[0.06] text-ink/55 transition hover:bg-ink/[0.12]"
            >
              <X size={14} strokeWidth={2.4} />
            </button>
          </header>

          <AnimatePresence mode="wait">
            <motion.div
              key={step.key}
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -14 }}
              transition={{ duration: 0.2 }}
              className="px-5 pb-2 pt-5 sm:px-7"
            >
              <span
                className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl"
                style={{ background: `${step.tint}1a`, color: step.tint }}
              >
                <Icon size={22} strokeWidth={2.2} />
              </span>
              <h2 className="font-display text-[21px] font-800 leading-tight tracking-[-0.02em] text-ink">
                {t(`tour.steps.${step.key}.title`)}
              </h2>
              <p className="mt-2.5 text-[14px] leading-[1.6] text-ink/70">
                {t(`tour.steps.${step.key}.body`)}
              </p>
              <p className="mt-4 rounded-2xl bg-ink/[0.04] px-3.5 py-2.5 font-mono text-[11px] leading-[1.5] text-ink/60">
                {t(`tour.steps.${step.key}.where`)}
              </p>
            </motion.div>
          </AnimatePresence>

          <footer className="flex items-center gap-3 px-5 pb-5 pt-3 sm:px-7">
            <div className="flex flex-1 items-center gap-1.5">
              {steps.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`${i + 1}`}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: i === index ? 18 : 6,
                    background: i === index ? '#ec4899' : 'rgba(10,18,38,0.15)',
                  }}
                />
              ))}
            </div>
            {index > 0 && (
              <button
                type="button"
                onClick={() => setIndex((i) => Math.max(i - 1, 0))}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-600 text-ink/60 transition hover:text-ink"
              >
                <ArrowLeft size={14} strokeWidth={2.4} />
                {t('tour.prev')}
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                onClick={() => {
                  close();
                  navigate(cm ? '/automatisations' : '/audit');
                }}
                className="btn-primary px-5 py-2.5 text-[13px]"
              >
                {t('tour.finish')}
                <ArrowRight size={14} strokeWidth={2.4} className="ml-1.5 inline" />
              </button>
            ) : (
              <button
                type="button"
                autoFocus
                onClick={() => setIndex((i) => Math.min(i + 1, steps.length - 1))}
                className="btn-primary px-5 py-2.5 text-[13px]"
              >
                {t('tour.next')}
                <ArrowRight size={14} strokeWidth={2.4} className="ml-1.5 inline" />
              </button>
            )}
          </footer>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
